/* iter55f56 — First-login prompt-mode picker.
 *
 * The very first time an authenticated user lands inside `/app`, we
 * ask how much hand-holding they want. Three options:
 *   • Guided — full tool tips + "what to do next" prompts. Runs the
 *              4-step onboarding walkthrough (Load manuscript / Load
 *              synopsis / Load framework → AI read → Bibles → Chapters).
 *   • Light  — only "what to do next" prompts; skip the tool tips
 *              that veterans find noisy.
 *   • Off    — no prompts at all. Users can flip this back on later
 *              from Settings.
 *
 * iter-reask-v1 · Sep-2026 — this now re-appears on EVERY login for
 * Guided/Light users (not just the first-ever), so returning writers
 * get a chance to change their mind each session. It is permanently
 * suppressed only once a writer picks "Off" — either here or via the
 * Settings → Preferences toggle. The 4-step intro tour + Training
 * Center auto-open still fire only on the true first-ever pick
 * (tracked separately via `pw_prompt_mode_first_pick_done`), so a
 * later re-pick never replays them.
 *
 * iter-reask-freq · Sep-2026 — Settings → Preferences also lets a
 * writer throttle the re-ask itself to "Once a month" instead of
 * every login (`pw_prompt_reask_frequency`); see `dueToReask()`.
 *
 * The choice is persisted in `localStorage` under `pw_prompt_mode`
 * (backend persistence can be layered in later without touching
 * consumers — they all read via `getPromptMode()` below).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Compass, Feather, EyeOff } from 'lucide-react';
import { useAuth } from '@/lib/auth_context';
import { runTour, runWhatsNewIfDue, COACHING_VERSION, getCoachingSeenVersion } from '@/lib/tours';

const KEY = 'pw_prompt_mode';
// iter-reask-v1 · Sep-2026 — separate from `pw_prompt_mode` itself: tracks
// whether the writer has EVER gone through the picker once (from either
// this dialog or Settings → Preferences), so a later re-ask on login #2+
// only re-shows the picker without replaying the 4-step intro tour or
// auto-opening the Training Center a second time.
const FIRST_PICK_KEY = 'pw_prompt_mode_first_pick_done';
// iter-reask-freq · Sep-2026 — "How often should we ask again?" set in
// Settings → Preferences. 'every_login' (default) re-shows the picker on
// every login; 'monthly' throttles it to once every 30 days.
const FREQ_KEY = 'pw_prompt_reask_frequency';
const LAST_ASKED_KEY = 'pw_prompt_mode_last_asked_at';
const MONTH_MS = 30 * 24 * 60 * 60 * 1000;

export function getPromptMode() {
  try { return window.localStorage.getItem(KEY) || null; }
  catch { return null; }
}

export function hasEverChosenPromptMode() {
  try { return Boolean(window.localStorage.getItem(FIRST_PICK_KEY)); }
  catch { return false; }
}

export function getReaskFrequency() {
  try { return window.localStorage.getItem(FREQ_KEY) || 'every_login'; }
  catch { return 'every_login'; }
}

export function setReaskFrequency(freq) {
  try { window.localStorage.setItem(FREQ_KEY, freq); }
  catch { /* localStorage blocked */ }
}

// Should the picker re-appear right now, given the frequency setting?
// `every_login` always says yes; `monthly` only once 30 days have
// passed since it was last actually shown.
function dueToReask() {
  if (getReaskFrequency() !== 'monthly') return true;
  try {
    const last = parseInt(window.localStorage.getItem(LAST_ASKED_KEY) || '0', 10);
    return !last || (Date.now() - last) >= MONTH_MS;
  } catch { return true; }
}

function markAskedNow() {
  try { window.localStorage.setItem(LAST_ASKED_KEY, String(Date.now())); }
  catch { /* ignore */ }
}

export function setPromptMode(mode) {
  try {
    window.localStorage.setItem(KEY, mode);
    window.localStorage.setItem(FIRST_PICK_KEY, '1');
    window.dispatchEvent(new CustomEvent('loom:prompt-mode-changed', { detail: { mode } }));
  }
  catch { /* localStorage blocked */ }
  // iter55f85 — Sync the preference to the backend so analytics
  // ingest can enforce opt-out server-side. Best-effort — a network
  // hiccup here shouldn't block the writer's UX.
  try {
    const axios = require('axios');
    const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
    axios.put(`${API}/analytics/preference`, { mode }, { withCredentials: true })
      .catch(() => {});
  } catch { /* silent */ }
}

function useOptions(t) {
  return [
    { id: 'guided', icon: Compass, title: t('onboardingPrompt.guidedTitle'),
      body:  t('onboardingPrompt.guidedBody') },
    { id: 'light',  icon: Feather, title: t('onboardingPrompt.lightTitle'),
      body:  t('onboardingPrompt.lightBody') },
    { id: 'off',    icon: EyeOff,  title: t('onboardingPrompt.offTitle'),
      body:  t('onboardingPrompt.offBody') },
  ];
}

export default function OnboardingPromptModeDialog() {
  const { t } = useTranslation();
  const OPTIONS = useOptions(t);
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const decidedRef = useRef(false);
  const scheduledRef = useRef(false);

  useEffect(() => {
    // iter226 — Whenever this effect decides the picker won't show
    // (admin, permanently off, or not due for a reask), tell anything
    // waiting on the onboarding chain (WelcomeOverlay) that there's
    // nothing coming — otherwise it would wait forever for a
    // 'getting-started-wizard-open'/'-done' pair that never fires.
    // When the picker DOES show, `choose()` below owns dispatching
    // 'onboarding-chain-complete' once the wizard (and, for a true
    // first pick, the tour/Training Center) has resolved. Wait for
    // `user` to actually load before deciding anything — a premature
    // "nothing's coming" while auth is still resolving would let
    // WelcomeOverlay open right before the picker pops up on top of it.
    //
    // `decidedRef` makes this a ONE-SHOT decision per mount: once we
    // decide to show the dialog, `markAskedNow()` flips `dueToReask()`
    // to false — if this effect re-ran afterwards (e.g. `user`'s
    // object reference changing on an unrelated context update) it
    // would otherwise re-evaluate, fall into the "not due" branch, and
    // fire a premature 'onboarding-chain-complete' while the dialog
    // (and the wizard chain behind it) is still in flight. `[user]`
    // itself can legitimately change reference a few times while auth
    // settles, so this effect can re-run several times post-decision —
    // `scheduledRef` + no cleanup-driven clearTimeout ensure the 900ms
    // show-the-dialog timer, once scheduled, always fires rather than
    // being torn down by one of those later re-runs.
    if (!user || decidedRef.current) return undefined;
    const skip = () => window.dispatchEvent(new CustomEvent('onboarding-chain-complete'));
    if (user.is_admin) { decidedRef.current = true; skip(); return undefined; }  // don't nag admins
    if (getPromptMode() === 'off') { decidedRef.current = true; skip(); return undefined; }  // permanently suppressed
    if (!dueToReask()) { decidedRef.current = true; skip(); return undefined; }  // "Ask me: Once a month" hasn't hit 30 days yet
    decidedRef.current = true;
    if (scheduledRef.current) return undefined;
    scheduledRef.current = true;
    // Slight delay so the app has settled before the modal takes over.
    setTimeout(() => { markAskedNow(); setOpen(true); }, 900);
    return undefined;
  }, [user]);

  /* iter-coaching-refresh · Feb-2026 — Returning-user "what's new" pass.
   *
   * When a Guided or Light user logs back in and the tour catalogue has
   * a newer `COACHING_VERSION` than the version they last saw, fire the
   * `whats-new` walkthrough once. This is how we teach any renamed
   * button or newly-shipped feature to writers who already opted into
   * coaching at their first login.
   *
   * Off-mode users are respected by `runWhatsNewIfDue` itself — the
   * effect still runs but the function short-circuits.
   */
  useEffect(() => {
    if (!user) return;
    const mode = getPromptMode();
    if (!mode || mode === 'off') return;                    // no coaching selected
    if (getCoachingSeenVersion() === COACHING_VERSION) return;
    // Give the app ~2s to settle so anchors have a chance to mount
    // before the spotlight tries to find them.
    const t = setTimeout(() => runWhatsNewIfDue(), 2000);
    return () => clearTimeout(t);
  }, [user]);

  const firstPickModeRef = useRef(null);
  const choose = (mode) => {
    // iter-reask-v1 — capture BEFORE setPromptMode() flips the flag, so
    // we know whether this is the writer's true first-ever pick.
    const isFirstPick = !hasEverChosenPromptMode();
    setPromptMode(mode);
    setOpen(false);
    if (isFirstPick) {
      // Feb-2026 · Getting Started Wizard now owns the true first-run
      // moment (story setup → starting point → Chapter 1). The old
      // spotlight tour + Training Center auto-open still run — just
      // AFTER the wizard closes instead of racing it on a fixed timer
      // (see the 'getting-started-wizard-done' listener below).
      firstPickModeRef.current = mode;
      setTimeout(() => window.dispatchEvent(new CustomEvent('getting-started-wizard-open')), 400);
    } else {
      // iter227 — A REASK pick (not the true first pick) never queues
      // the wizard/tour/Training Center chain, so nothing was telling
      // WelcomeOverlay "you're clear to open" — it deadlocked forever
      // for a returning Guided/Light writer on a still-empty project.
      setTimeout(() => window.dispatchEvent(new CustomEvent('onboarding-chain-complete')), 1200);
    }
  };

  useEffect(() => {
    const onWizardDone = () => {
      const mode = firstPickModeRef.current;
      firstPickModeRef.current = null;
      if (mode === 'off') {
        // Nothing else queued — the import/template overlay can show now.
        window.dispatchEvent(new CustomEvent('onboarding-chain-complete'));
        return;
      }
      // Kick off the 4-step walkthrough for guided / light users — once.
      setTimeout(() => runTour('onboarding-start', { force: true }), 400);
      // iter-training-v-ach-coach — After the intro walkthrough settles,
      // auto-open the Training Center so brand-new writers see the
      // curriculum instead of a blank desk. Guarded by a "seen" flag
      // so we don't re-launch on every login.
      let trainingCenterQueued = false;
      try {
        if (!window.localStorage.getItem('pw_training_center_seen_v1')) {
          trainingCenterQueued = true;
          setTimeout(() => {
            try {
              window.localStorage.setItem('pw_training_center_seen_v1', String(Date.now()));
              window.dispatchEvent(new CustomEvent('training-center-open'));
            } catch { /* ignore */ }
          }, 3200);
        }
      } catch { /* ignore */ }
      // iter226 — Hold the import/template overlay back until the tour
      // and (if queued) the Training Center have had their moment, so a
      // brand-new Guided/Light writer never gets 2-3 modals stacked at
      // once. No clean "tour/training-center closed" signal exists, so
      // this mirrors the same fixed-delay convention already used
      // above — generous enough that both have opened and the writer
      // has had a beat to dismiss them.
      setTimeout(() => window.dispatchEvent(new CustomEvent('onboarding-chain-complete')),
        trainingCenterQueued ? 4200 : 1200);
    };
    window.addEventListener('getting-started-wizard-done', onWizardDone);
    return () => window.removeEventListener('getting-started-wizard-done', onWizardDone);
  }, []);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) choose('off'); }}>
      <DialogContent className="max-w-lg" data-testid="onboarding-prompt-mode-dialog">
        <DialogHeader>
          <DialogTitle>{t('onboardingPrompt.title')}</DialogTitle>
          <DialogDescription>
            {t('onboardingPrompt.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 py-2">
          {OPTIONS.map(({ id, icon: Icon, title, body }) => (
            <button
              key={id}
              onClick={() => choose(id)}
              data-testid={`onboarding-prompt-mode-${id}`}
              className="flex items-start gap-3 rounded-md border border-border p-3 text-left transition hover:border-primary hover:bg-primary/5"
            >
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <div className="text-[13px] font-semibold">{title}</div>
                <div className="text-[11.5px] text-muted-foreground">{body}</div>
              </div>
            </button>
          ))}
        </div>
        <DialogFooter className="text-[11px] text-muted-foreground">
          {t('onboardingPrompt.footerNote')}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
